import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { MdCheck } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable, { DataTableColumn } from '../DataTable';
import RowActionsDropdown, { RowAction } from '../RowActionsDropdown';
import {
  useDeleteModelMutation,
  useGetAllModelsQuery,
  useRestoreModelMutation,
} from '../tools.service';
import { ModelType } from '../tools.types';
import { formatAiProviderName } from '../../../utils/aiProvider';
import { formatToolIdName } from '../../../utils/toolId';
import { isFetchBaseQueryError } from '../../../utils/helpers';
import routes from '../../../routes';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { usePagedList } from '../../../hooks/usePagedList';
import PaginationBar from '../../../UIComponents/PaginationBar';
import { useAppDispatch } from '../../../hooks/defaultHooks';
import { openModal } from '../../../store/slices/modalSlice';
import cls from './ModelsTab.module.scss';

const ModelsTab = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [pendingModelId, setPendingModelId] = useState('');
  const [deleteModel] = useDeleteModelMutation();
  const [restoreModel] = useRestoreModelMutation();
  useDocumentTitle('Models');
  const { page, size, setPage, setSize } = usePagedList({});
  const { data, isLoading } = useGetAllModelsQuery({ page, size });
  const deleteModelHandler = async (model: ModelType) => {
    setPendingModelId(model.id);
    try {
      await deleteModel(model.id).unwrap();
      toast.success('Model deleted');
    } catch (error) {
      if (isFetchBaseQueryError(error) && error.status === 403) toast.error('Access denied');
      else toast.error('Failed to delete the model');
    } finally {
      setPendingModelId('');
    }
  };
  const restoreModelHandler = async (model: ModelType) => {
    setPendingModelId(model.id);
    try {
      await restoreModel(model.id).unwrap();
      toast.success('Model restored');
    } catch (error) {
      if (isFetchBaseQueryError(error) && error.status === 403) toast.error('Access denied');
      else toast.error('Failed to restore the model');
    } finally {
      setPendingModelId('');
    }
  };
  const confirmDeleteHandler = (model: ModelType) =>
    dispatch(
      openModal({
        type: 'actionWithTool',
        extra: {
          id: model.id,
          title: 'Delete model?',
          supportText: 'The model',
          textInformation: model.name,
          subSupportText: 'will become unavailable to users',
          handler: () => deleteModelHandler(model),
          actionButtonColor: 'danger',
          actionButtonText: 'Delete',
        },
      }),
    );
  const confirmRestoreHandler = (model: ModelType) =>
    dispatch(
      openModal({
        type: 'actionWithTool',
        extra: {
          id: model.id,
          title: 'Restore model?',
          supportText: 'The model',
          textInformation: model.name,
          subSupportText: 'will become available to users again',
          handler: () => restoreModelHandler(model),
          actionButtonColor: 'primary',
          actionButtonText: 'Restore',
        },
      }),
    );
  const actionsOf = (model: ModelType): RowAction[] =>
    model.deleted
      ? [
          {
            label: 'Restore',
            onSelect: () => confirmRestoreHandler(model),
            disabled: pendingModelId === model.id,
          },
        ]
      : [
          { label: 'Edit', onSelect: () => navigate(routes.pages.updateModelPageById(model.id)) },
          {
            label: 'Delete',
            onSelect: () => confirmDeleteHandler(model),
            danger: true,
            disabled: pendingModelId === model.id,
          },
        ];
  const columns: DataTableColumn<ModelType>[] = [
    { key: 'name', title: 'Name', render: (model) => model.name },
    { key: 'model', title: 'Model', width: 'minmax(160px, 1.5fr)', render: (model) => model.model },
    {
      key: 'providerId',
      title: 'Provider',
      width: 'minmax(110px, 0.8fr)',
      render: (model) => formatAiProviderName(model.providerId),
    },
    {
      key: 'maxNumberToGenerate',
      title: 'Max per request',
      width: 'minmax(120px, 0.8fr)',
      render: (model) => model.payload?.maxNumberToGenerate ?? '-',
    },
    {
      key: 'aspectRatio',
      title: 'Aspect ratio',
      width: 'minmax(140px, 1fr)',
      render: (model) => model.payload?.aspectRatio?.map((ratio) => ratio.name).join(', ') || '-',
    },
    {
      key: 'publishedTools',
      title: 'Published',
      width: 'minmax(120px, 1fr)',
      // where a client can generate with it; a dash is a model no client sees
      render: (model) => (model.publishedTools ?? []).map(formatToolIdName).join(', ') || '-',
    },
    {
      key: 'sandbox',
      title: 'Sandbox',
      width: 'minmax(90px, 0.5fr)',
      render: (model) =>
        model.sandbox ? <MdCheck className={cls.SandboxIcon} aria-label="Sandbox" /> : '-',
    },
    {
      key: 'deleted',
      title: 'Status',
      width: 'minmax(90px, 0.5fr)',
      render: (model) => (
        <span className={model.deleted ? cls.Deleted : cls.Active}>
          {model.deleted ? 'Deleted' : 'Active'}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: '56px',
      allowOverflow: true,
      render: (model) => <RowActionsDropdown id={model.id} actions={actionsOf(model)} />,
    },
  ];
  return (
    <div className={cls.Container}>
      <div className={cls.Actions}>
        <Button className={cls.CreateButton} onClick={() => navigate(routes.pages.newModelPage())}>
          Add model
        </Button>
      </div>
      <DataTable
        columns={columns}
        items={data?.models}
        isLoading={isLoading}
        emptyText="No models found"
        rowKey={(model) => model.id}
        onRowClick={(model) => navigate(routes.pages.modelPageById(model.id))}
        footer={
          data && (
            <PaginationBar
              page={page}
              size={size}
              countAll={data.countAll}
              onPageChange={setPage}
              onSizeChange={setSize}
              itemsName="models"
            />
          )
        }
      />
    </div>
  );
};

export default ModelsTab;
